import { useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useQuery } from 'react-query';
import { fetcher } from '../../../utils/api';
import { ApiRoutes } from '../../../constants/routes';
import { useAuthProvider } from '../../../utils/auth';

// Who a post is made as: yourself, an artist (act) you run, or a label or
// publisher you run or speak for. Choices are keys: 'me', 'act-12',
// 'label-3', 'publisher-5'.
//   fixed: a key that can't be changed (an artist's own dashboard)
export const usePostAs = (fixed = null) => {
  const { user, userType, workingAs } = useAuthProvider();
  const { data: acts = [] } = useQuery({
    queryKey: ['user-acts'],
    queryFn: () => fetcher(ApiRoutes.CreatorActs),
    enabled: !!user && userType === 'creator' && !fixed,
  });
  const actList = Array.isArray(acts) ? acts : acts?.results || acts?.data || [];
  const options = [
    { key: 'me', label: 'Myself' },
    ...actList.map((act) => ({ key: `act-${act.id}`, label: act.name })),
    ...(user?.organizations?.labels || []).map((org) => ({ key: `label-${org.id}`, label: `${org.name} (label)` })),
    ...(user?.organizations?.publishers || []).map((org) => ({ key: `publisher-${org.id}`, label: `${org.name} (publisher)` })),
  ];
  // Working as a label or publisher: post as it unless they choose otherwise
  const preferred = fixed || (workingAs ? `${workingAs.kind}-${workingAs.id}` : 'me');
  const [postAs, setPostAs] = useState(preferred);
  useEffect(() => setPostAs(preferred), [preferred]);
  return { postAs, setPostAs, options, fixed: !!fixed };
};

// The post's author fields for the API
export const authorFields = (postAs) => {
  const [kind, id] = String(postAs || 'me').split('-');
  return kind === 'me' || !id ? {} : { [`${kind}_id`]: Number(id) };
};

const PostAsSelect = ({ postAs, setPostAs, options, fixed }) => {
  if (fixed || options.length < 2) return null;
  return (
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <InputLabel id="post-as-label">Post as</InputLabel>
      <Select
        labelId="post-as-label"
        id="post-as"
        label="Post as"
        value={options.some((option) => option.key === postAs) ? postAs : 'me'}
        onChange={(event) => setPostAs(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default PostAsSelect;
